import React, { useEffect, useState } from 'react'
import { api } from '@pilot-hub/api-client'
import { drohnenWind, type DrohnenWind } from '../utils/drohnenWind'
import { Card } from '@pilot-hub/ui'
import { Sun, Thermometer, Droplets, Wind, Zap, Compass, Cloud, Eye, Gauge } from 'lucide-react'

export interface WeatherData {
  temperature: number
  dewpoint: number
  windspeed: number
  windgusts: number
  winddirection: number
  precipitation_probability: number
  cloudcover: number
  visibility: number
  uv_index: number
  /** geomagnetischer Kp-Index (NOAA SWPC); null, wenn nicht abrufbar */
  kp_index: number | null
  time: string
  /** Datenquelle vom Server: 'open-meteo' | 'brightsky' (DWD) | 'cache-veraltet' */
  quelle?: string
  /** Zeitpunkt der Abfrage beim Anbieter (ISO) */
  stand?: string
}

// Wetter-Warnschwellen (Feedback #1): Kachel wird rot, wenn der Wert strikt größer ist
export const BOEEN_ROT_MS = 12   // Böen in m/s (Open-Meteo liefert km/h -> Umrechnung durch 3,6)
export const KP_GELB = 4         // geomagnetischer Kp-Index: ab 4 gelb
export const KP_ROT = 5          // ab 5 rot
export const boeenRot = (kmh: number | null | undefined) => kmh != null && Math.round((kmh / 3.6) * 100) / 100 > BOEEN_ROT_MS
export const kpRot = (kp: number | null | undefined) => kp != null && kp >= KP_ROT
export const kpGelb = (kp: number | null | undefined) => kp != null && kp >= KP_GELB && kp < KP_ROT
const KACHEL_NORMAL = 'p-3 rounded-lg bg-dark-bg-tertiary/50'
const KACHEL_ROT = 'p-3 rounded-lg border border-red-500/60 bg-red-500/15'
const KACHEL_GELB = 'p-3 rounded-lg border border-yellow-500/60 bg-yellow-500/15'

/** Wind am Fluggerät (geschätzt aus der Drohnen-Telemetrie): nur sichtbar, wenn eine Drohne in der Luft aktuell sendet.
 *  Mehrere Drohnen: die erste in der Luft mit frischen Windwerten (/dji/live liefert alle Drohnen, nicht einsatzbezogen). */
function DrohnenWindKachel() {
  const [wind, setWind] = useState<(DrohnenWind & { drohne: string }) | null>(null)
  useEffect(() => {
    let aktiv = true
    const holen = () => {
      if (document.visibilityState !== 'visible') return
      api.get('/dji/live').then(r => {
        if (!aktiv) return
        for (const d of (r.data as { in_der_luft: boolean; alter_s: number; drohne: string; kennzeichen: string | null; daten: any }[]) ?? []) {
          const w = d.in_der_luft && d.alter_s <= 10 ? drohnenWind(d.daten) : null
          if (w) { setWind({ ...w, drohne: d.kennzeichen || d.drohne }); return }
        }
        setWind(null)
      }).catch(() => { /* Wetter bleibt unabhängig von der Drohnen-Abfrage */ })
    }
    holen()
    const t = window.setInterval(holen, 5000)
    return () => { aktiv = false; window.clearInterval(t) }
  }, [])
  if (!wind) return null
  return (
    <div className="p-3 rounded-lg bg-dark-bg-tertiary/50" data-testid="wetter-drohnenwind">
      <div className="flex items-center gap-2 text-dark-text-muted text-xs">
        <Wind className="h-4 w-4" />
        <span>Wind am Fluggerät (geschätzt, Drohne{wind.drohne ? ` ${wind.drohne}` : ''})</span>
      </div>
      <div className="text-xl font-bold text-dark-text">
        {wind.kmh.toFixed(1).replace('.', ',')} km/h ({wind.ms.toFixed(1).replace('.', ',')} m/s){wind.richtung ? ` · ${wind.richtung}` : ''}
      </div>
    </div>
  )
}

export default function WeatherCard({ data, className, hinweis, onRetry }: { data: WeatherData | null; className?: string; hinweis?: string | null; onRetry?: () => void }) {
  if (!data && hinweis) {
    return (
      <Card className={`p-4 ${className || ''}`}>
        <div className="flex items-center gap-2 mb-4">
          <Sun className="h-5 w-5 text-yellow-400" />
          <h3 className="font-semibold text-dark-text">Wetter</h3>
        </div>
        <div className="text-center py-8 text-dark-text-muted space-y-3" data-testid="wetter-hinweis">
          <p>{hinweis}</p>
          {onRetry && <button type="button" onClick={onRetry} className="px-4 py-2 rounded-lg bg-dark-bg-tertiary/60 text-dark-text text-sm min-h-[44px]">Erneut versuchen</button>}
        </div>
      </Card>
    )
  }
  if (!data) {
    return (
      <Card className={`p-4 ${className || ''}`}>
        <div className="flex items-center gap-2 mb-4">
          <Sun className="h-5 w-5 text-yellow-400" />
          <h3 className="font-semibold text-dark-text">Wetter</h3>
        </div>
        <div className="text-center py-8 text-dark-text-muted">Lade Wetterdaten…</div>
      </Card>
    )
  }

  const windDir = data.winddirection
  const windDirText = windDir == null ? '—' : [
    'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
  ][Math.round(windDir / 22.5) % 16]

  return (
    <Card className={`p-4 ${className || ''}`}>
      <div className="flex items-center gap-2 mb-4">
        <Sun className="h-5 w-5 text-yellow-400" />
        <h3 className="font-semibold text-dark-text">Wetter (lokal)</h3>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Thermometer className="h-4 w-4" />
              <span>Temp.</span>
            </div>
            <div className="text-xl font-bold text-dark-text">{data.temperature.toFixed(1)}°C</div>
          </div>
          <div className="p-3 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Droplets className="h-4 w-4" />
              <span>Taupunkt</span>
            </div>
            <div className="text-xl font-bold text-dark-text">{data.dewpoint != null ? data.dewpoint.toFixed(1) : '—'}°C</div>
          </div>
          <div className="p-3 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Wind className="h-4 w-4" />
              <span>Wind</span>
            </div>
            <div className="text-xl font-bold text-dark-text">{data.windspeed != null ? data.windspeed.toFixed(1) : '—'} km/h</div>
          </div>
          <div className={boeenRot(data.windgusts) ? KACHEL_ROT : KACHEL_NORMAL} data-testid="wetter-boeen" data-rot={boeenRot(data.windgusts)}>
            <div className={`flex items-center gap-2 text-xs ${boeenRot(data.windgusts) ? 'text-red-300' : 'text-dark-text-muted'}`}>
              <Zap className="h-4 w-4" />
              <span>Böen</span>
            </div>
            <div className={`text-xl font-bold ${boeenRot(data.windgusts) ? 'text-red-400' : 'text-dark-text'}`}
              title="Rot ab Böen über 12 m/s (43,2 km/h)">{data.windgusts != null ? data.windgusts.toFixed(1) : '—'} km/h</div>
          </div>
          <div className="p-3 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Compass className="h-4 w-4" />
              <span>Richtung</span>
            </div>
            <div className="text-xl font-bold text-dark-text">{windDir != null ? `${windDirText} (${windDir}°)` : '—'}</div>
          </div>
          <div className="p-3 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Cloud className="h-4 w-4" />
              <span>Bewölkung</span>
            </div>
            <div className="text-xl font-bold text-dark-text">{data.cloudcover != null ? `${data.cloudcover}%` : '—'}</div>
          </div>
          <div className="p-3 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Droplets className="h-4 w-4" />
              <span>Niederschlag</span>
            </div>
            <div className="text-xl font-bold text-dark-text">{data.precipitation_probability != null ? `${data.precipitation_probability}%` : '—'}</div>
          </div>
          <div className="p-3 rounded-lg bg-dark-bg-tertiary/50">
            <div className="flex items-center gap-2 text-dark-text-muted text-xs">
              <Eye className="h-4 w-4" />
              <span>Sichtweite</span>
            </div>
            <div className="text-xl font-bold text-dark-text">{data.visibility != null ? `${data.visibility} km` : '—'}</div>
          </div>
          <div className={kpRot(data.kp_index) ? KACHEL_ROT : kpGelb(data.kp_index) ? KACHEL_GELB : KACHEL_NORMAL} data-testid="wetter-kp" data-rot={kpRot(data.kp_index)} data-gelb={kpGelb(data.kp_index)}>
            <div className={`flex items-center gap-2 text-xs ${kpRot(data.kp_index) ? 'text-red-300' : kpGelb(data.kp_index) ? 'text-yellow-300' : 'text-dark-text-muted'}`}>
              <Gauge className="h-4 w-4" />
              <span>KP-Index</span>
            </div>
            <div className={`text-xl font-bold ${kpRot(data.kp_index) ? 'text-red-400' : kpGelb(data.kp_index) ? 'text-yellow-400' : 'text-dark-text'}`}
              title="Geomagnetische Aktivität (NOAA). Gelb ab Kp 4, rot ab Kp 5; ab Kp 5 kann die GNSS-Genauigkeit der Drohne leiden.">
              {data.kp_index != null ? data.kp_index.toFixed(1).replace('.', ',') : '—'}
            </div>
          </div>
        </div>
        <DrohnenWindKachel />
        <p className="text-xs text-dark-text-muted text-center" data-testid="wetter-stand">
          Stand: {new Date(data.time).toLocaleString('de-DE')}
          {data.quelle === 'brightsky' && ' · Quelle DWD'}
          {data.quelle === 'cache-veraltet' && data.stand && ` · veraltet (abgerufen ${new Date(data.stand).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })})`}
        </p>
      </div>
    </Card>
  )
}
